import React, { useCallback, useMemo } from 'react';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankOutlinedIcon from '@mui/icons-material/CheckBoxOutlineBlankOutlined';

import type { DefaultTheme } from '@mui/styles';

const checkBoxSx = (theme: DefaultTheme) => ({
  width: theme.spacing(2),
  height: theme.spacing(2),
});

interface Props {
  active: boolean;
  disabled?: boolean;
  children?: React.ReactNode;
  onChange: (active: boolean) => void;
  justifyContent?: 'flex-start' | 'center' | 'flex-end';
}

export default function CheckButton({
  active,
  children,
  disabled,
  onChange,
  justifyContent = 'flex-start',
}: Props) {
  const disabledSx = useMemo(
    () => ({
      cursor: { cursor: !disabled ? 'pointer' : 'default' },
      opacity: { opacity: disabled ? 0.38 : 1 },
    }),
    [disabled],
  );

  const handlePress = useCallback<React.MouseEventHandler<HTMLDivElement>>(
    (event) => {
      event.stopPropagation();
      if (!disabled) onChange(!active);
    },
    [active, disabled, onChange],
  );

  return (
    <Stack
      sx={disabledSx.cursor}
      alignItems="center"
      justifyContent={justifyContent}
      flexDirection="row"
      onClick={handlePress}
      gap={(theme) => theme.spacing(1)}
    >
      <Box sx={disabledSx.opacity} display="flex">
        {active ? (
          <CheckBoxIcon sx={checkBoxSx} />
        ) : (
          <CheckBoxOutlineBlankOutlinedIcon sx={checkBoxSx} />
        )}
      </Box>
      {children}
    </Stack>
  );
}
